import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { createProductValidationSchema } from '../../util/validateSchema';
import { getApiErrorMessage } from '../../util/getApiErrorMessage';
import { useCreateProductMutation } from '../../app/services/product';

interface CreateProductDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (product: Product) => void;
}

const CreateProductDialog: React.FC<CreateProductDialogProps> = ({ isOpen, onClose, onCreated }) => {
  const [createProduct, { isLoading }] = useCreateProductMutation();

  const formik = useFormik({
    initialValues: {
      productName: '',
      productType: 'Web',
      description: '',
    } as CreateProduct,
    validationSchema: createProductValidationSchema,
    onSubmit: async (values) => {
      const request: CreateProduct = {
        ...values,
        productName: values.productName.trim(),
        description: values.description.trim(),
      };
      const payload: any = await createProduct(request);
      if (payload?.data) {
        toast.success('Product created successfully');
        onCreated(payload.data);
        handleClose();
      } else {
        toast.error(getApiErrorMessage(payload) || 'Failed to create product');
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Product</DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            fullWidth
            autoFocus
            label="Product Name"
            name="productName"
            size="small"
            value={formik.values.productName}
            error={Boolean(formik.touched.productName && formik.errors.productName)}
            helperText={formik.touched.productName && formik.errors.productName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          <TextField
            select
            fullWidth
            label="Product Type"
            name="productType"
            size="small"
            value={formik.values.productType}
            error={Boolean(formik.touched.productType && formik.errors.productType)}
            helperText={formik.touched.productType && formik.errors.productType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <MenuItem value="Web">Web</MenuItem>
            <MenuItem value="Mobile">Mobile</MenuItem>
            <MenuItem value="Desktop">Desktop</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Description"
            name="description"
            size="small"
            multiline
            rows={4}
            value={formik.values.description}
            error={Boolean(formik.touched.description && formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={isLoading}
          onClick={() => formik.handleSubmit()}
        >
          Create
          {isLoading && <CircularProgress size={20} sx={{ ml: 1 }} />}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProductDialog;
